/**
 * The named wait, so a minute of model work is not a spinner.
 *
 * Reading a prescription runs as its own workflow and takes a minute or two.
 * It once happened inside the upload request behind a word that never changed,
 * and the only way to tell "working" from "stuck" was to wait and find out.
 * Saying which half it is on is the difference.
 *
 * Lives in `ingredients/` because the simulation editor is the screen that
 * waits, and it is no longer the screen that uploads. Moving the upload to its
 * own page silently took this with it -- the editor showed a heading and the
 * page image, which reads as finished rather than working.
 */

/** What the preparation is doing, in the order it does it. */
export const PREPARING: Readonly<Record<string, string>> = {
  preparing: 'Preparando…',
  detecting: 'Localizando o texto da receita',
  reading: 'Lendo a receita',
}

export function Preparing({ status, failed }: { status: string; failed: boolean }) {
  if (failed) {
    return (
      <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-2 border p-5">
        <h2 className="text-tH6 text-text-strong">Não foi possível ler a receita</h2>
        <p className="text-tP text-text-weak">
          Escolha outro arquivo, ou tente de novo — a leitura é reexecutável.
        </p>
      </div>
    )
  }

  const steps = Object.entries(PREPARING).filter(([key]) => key !== 'preparing')
  const current = Object.keys(PREPARING).indexOf(status)

  return (
    <div className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-5">
      <h2 className="text-tH6 text-text-strong">{PREPARING[status] ?? 'Preparando…'}</h2>
      <ol className="flex flex-col gap-2" aria-label="Preparação da simulação">
        {steps.map(([key, text], index) => {
          const place = Object.keys(PREPARING).indexOf(key)
          const done = place < current
          const active = place === current
          return (
            <li
              key={key}
              aria-current={active ? 'step' : undefined}
              className={`text-tP ${
                done ? 'text-text-weak' : active ? 'text-text-strong' : 'text-text-disabled'
              }`}
            >
              {done ? '✓ ' : active ? '• ' : '· '}
              {text}
              {index === steps.length - 1 && active && (
                <span className="text-text-weak"> — costuma levar um a dois minutos</span>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}
